// store/useCandidateStore.ts
import { create } from "zustand";

const baseUrl = "https://admin.talentlinkindo.com/api";

// ====================
// Types
// ====================
export type Candidate = {
  id: number | string;
  name: string;
  photo?: string;
  positionApplied?: string;
  cv?: string;
  email?: string;
  phoneNumber?: string;
  location?: string;
  nationality?: string;
  join?: string;
  yearExp?: string;
  birthDate?: string;
  languages?: string;
  aboutMe?: string;
  currentSalary?: number;
  expectedSalary?: number;
  educations?: any[];
  experiences?: any[];
  skills?: string[];
  certifications?: any[];
  resume?: { file?: string; url?: string };
};

export type PaginationMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};


export type CandidateStatus =
  | "applied"
  | "reviewed"
  | "interviewed"
  | "hired"
  | "rejected";

export type SidebarInfo = {
  vacancy_name: string;
  created_at: string;
  expired_at: string;
  status_counts: { status: CandidateStatus; label: string; total: number }[];
  total_applications: number;
};


// ====================
// Store type
// ====================
type CandidateStore = {
  sidebarInfo: SidebarInfo | null;
  candidates: Record<CandidateStatus, Candidate[]>;
  pagination: Record<CandidateStatus, PaginationMeta | null>;
  candidateDetail: Candidate | null;
  loading: boolean;
  error: string | null;

  updateCandidateStatus: (
    applicationId: number | string,
    vacancyId: string,
    status: CandidateStatus
  ) => Promise<void>;

  fetchSidebarInfo: (vacancyId: string) => Promise<void>;
  fetchCandidatesByStatus: (
    vacancyId: string,
    status: CandidateStatus,
    page?: number
  ) => Promise<void>;
  fetchCandidateDetail: (candidateId: string) => Promise<void>;
};


// ====================
// Helper
// ====================
const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.state?.accessToken ?? null;
  } catch (e) {
    console.error("❌ Failed to parse auth token:", e);
    return null;
  }
};

const getHeaders = () => {
  const token = getAccessToken();
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
};

const handleUnauthorized = () => {
  localStorage.removeItem("auth-storage");
  window.location.href = "/";
};

// ====================
// Store
// ====================
export const useCandidateStore = create<CandidateStore>((set, get) => ({
  sidebarInfo: null,
  candidates: {
    applied: [],
    reviewed: [],
    interviewed: [],
    hired: [],
    rejected: [],
  },
  candidateDetail: null,
  loading: false,
  error: null,
  pagination: {
  applied: null,
  reviewed: null,
  interviewed: null,
  hired: null,
  rejected: null,
},


  fetchSidebarInfo: async (vacancyId) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${baseUrl}/company/vacancies/${vacancyId}/sidebar-info`, {
        headers: getHeaders(),
      });
      if (res.status === 401) return handleUnauthorized();
      const json = await res.json();
      set({ sidebarInfo: json.data, loading: false });
    } catch (err) {
      console.error("❌ Error fetchSidebarInfo:", err);
      set({ loading: false, error: "Failed to fetch sidebar info" });
    }
  },

  fetchCandidatesByStatus: async (vacancyId, status, page = 1) => {
    set({ loading: true, error: null });

    try {
      const res = await fetch(
        `${baseUrl}/company/vacancies/${vacancyId}/applications/filter/${status}?page=${page}`,
        { headers: getHeaders() }
      );

      if (res.status === 401) return handleUnauthorized();

      const json = await res.json();

      set((state) => ({
        candidates: {
          ...state.candidates,
          [status]: json.data || [],
        },
        pagination: {
          ...state.pagination,
          [status]: json.meta || null,
        },
        loading: false,
      }));
    } catch (err) {
      console.error("❌ Error fetchCandidatesByStatus:", err);
      set({ loading: false, error: "Failed to fetch candidates" });
    }
  },


  fetchCandidateDetail: async (candidateId) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${baseUrl}/company/candidates/${candidateId}`, {
        headers: getHeaders(),
      });
      if (res.status === 401) return handleUnauthorized();
      const json = await res.json();
      set({ candidateDetail: json.data, loading: false });
    } catch (err) {
      console.error("❌ Error fetchCandidateDetail:", err);
      set({ loading: false, error: "Failed to fetch candidate detail" });
    }
  },

  updateCandidateStatus: async (applicationId, vacancyId, status) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${baseUrl}/company/applications/${applicationId}/status`, {
        method: "PATCH",
        headers: getHeaders(),
        body: JSON.stringify({ status }),
      });

      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error("Failed to update status");

      // Setelah berhasil, update candidates di state
      const state = get();
      const allStatuses: CandidateStatus[] = ["applied", "reviewed", "interviewed", "hired", "rejected"];

      let updatedCandidates = { ...state.candidates };

      // Cari candidate yang diupdate di semua status, hapus dari status lama
      for (let s of allStatuses) {
        updatedCandidates[s] = updatedCandidates[s].filter((c) => c.id !== applicationId);
      }

      // Ambil data candidate yang diupdate (bisa dari API atau dari state lama)
      const updatedCandidate = state.candidates[status].find((c) => c.id === applicationId) || null;
      if (updatedCandidate) {
        updatedCandidates[status] = [...updatedCandidates[status], updatedCandidate];
      } else {
        // kalau ga ada di status baru, ambil dari state sebelumnya
        for (let s of allStatuses) {
          const found = state.candidates[s].find((c) => c.id === applicationId);
          if (found) {
            updatedCandidates[status] = [...updatedCandidates[status], found];
            break;
          }
        }
      }

      set({ candidates: updatedCandidates, loading: false });
    } catch (err) {
      console.error("❌ Error updateCandidateStatus:", err);
      set({ loading: false, error: "Failed to update candidate status" });
    }
  },
}));
